import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Pixel-position drag for floating panels (tutorial card, etc.).
 *
 * ## RCA — why effect-based drag broke (and keeps coming back)
 *
 * The first tutorial drag implementation (`1013830f`) used:
 *   `useEffect(() => { window.addEventListener('mousemove', ...) }, [isDragging])`
 * That registers listeners **after** React commits `setDragging(true)` — one frame late.
 * The first pointer movements after mousedown therefore never run `onMove`, so the panel
 * feels immovable or only jumps after a pause. This is **not** resolved by reverting other
 * commits; re-introducing the same pattern will regress again.
 *
 * **Correct pattern:** register `pointermove` / `pointerup` on `document` in the **same
 * synchronous** `pointerdown` handler (this hook). Optionally `setPointerCapture` on the
 * handle. Use Pointer Events so mouse + touch work; set `touch-action: none` on the handle.
 *
 * ## Do not
 * - Attach drag listeners in `useEffect([isDragging])` or any deferred effect for this UX.
 * - Rely on `onMouseMove` on a parent unless that parent is guaranteed to receive all moves
 *   (Export Preview uses a full-screen wrapper — different pattern).
 *
 * @see web/frontend/src/components/TutorialOverlay.tsx
 */
export interface UseDraggablePanelPositionResult {
  dragPos: { x: number; y: number } | null;
  isDragging: boolean;
  /** Set true during drag — use to skip heavy per-frame work (e.g. overlay mask updates). */
  draggingRef: React.MutableRefObject<boolean>;
  handleDragHandlePointerDown: (e: React.PointerEvent<HTMLElement>) => void;
}

export function useDraggablePanelPosition(
  panelRef: React.RefObject<HTMLElement | null>,
  /** When this value changes, dragged position resets (e.g. tutorial `currentStep`). */
  resetPositionWhen: number | string
): UseDraggablePanelPositionResult {
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const draggingRef = useRef(false);

  useEffect(() => {
    setDragPos(null);
  }, [resetPositionWhen]);

  const handleDragHandlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const panel = panelRef.current;
      if (!panel) return;
      const r = panel.getBoundingClientRect();
      const start = { mouseX: e.clientX, mouseY: e.clientY, elX: r.left, elY: r.top };
      draggingRef.current = true;
      setIsDragging(true);
      const captureEl = e.currentTarget;
      const pid = e.pointerId;
      try {
        captureEl.setPointerCapture(pid);
      } catch {
        /* ignore */
      }

      const onMove = (ev: PointerEvent) => {
        const panel = panelRef.current;
        if (!panel) return;
        const margin = 8;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const pw = panel.offsetWidth;
        const ph = panel.offsetHeight;
        let nx = start.elX + (ev.clientX - start.mouseX);
        let ny = start.elY + (ev.clientY - start.mouseY);
        nx = Math.min(Math.max(margin, nx), vw - pw - margin);
        ny = Math.min(Math.max(margin, ny), vh - ph - margin);
        setDragPos({ x: nx, y: ny });
      };
      const onDone = () => {
        draggingRef.current = false;
        setIsDragging(false);
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onDone);
        document.removeEventListener('pointercancel', onDone);
        try {
          captureEl.releasePointerCapture(pid);
        } catch {
          /* ignore */
        }
      };
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onDone);
      document.addEventListener('pointercancel', onDone);
    },
    [panelRef]
  );

  return { dragPos, isDragging, draggingRef, handleDragHandlePointerDown };
}
